import React from "react";
import { MenuItem, Button, Tag } from "@blueprintjs/core";
import { observer } from "mobx-react-lite";
import { ItemPredicate, MultiSelect } from "@blueprintjs/select";
import { isoLangs } from "../../../../../../global/dist/misc/isoLangs";
import type { ILangItem } from "../../../data/user/UserLanguagesStore";

// LanguageaMultiSelect
const LanguageaMultiSelect = MultiSelect<ILangItem>;

/**
 * LanguageSelect
 */
export const LanguageSelect: React.FC<{
	readonly languages?: ILangItem[];
	readonly selected?: ILangItem[];
	readonly assignedLangCodes: string[];
	onSelect(item: ILangItem): void;
	onRemove(item: ILangItem): void;
	onClear?(): void;
}> = observer((props) => {
	// Languages
	const languages = React.useRef<ILangItem[]>(
		props.languages ??
			Object.keys(isoLangs).map((code) => ({
				label: isoLangs[code].name,
				value: code,
			}))
	);

	// Assigned items
	const selected = props.selected ?? props.assignedLangCodes.map((code) => languages.current.find((item) => item.value === code));

	// Item renderer
	const itemRenderer = (item: ILangItem) => {
		return (
			<MenuItem
				key={item.value}
				text={item.label}
				icon={props.assignedLangCodes.includes(item.value) ? "tick" : "blank"}
				onClick={props.onSelect.bind(null, item)}
			/>
		);
	};

	// Tag renderer
	const tagRenderer = (item: ILangItem) => {
		return <Tag key={item.value}>{item.label}</Tag>;
	};

	// Item predicate
	const itemPredicate: ItemPredicate<ILangItem> = (query, item, _index, exactMatch) => {
		const normalizedLabel = item.label.toLowerCase();
		const normalizedQuery = query.toLowerCase();

		if (exactMatch) {
			return normalizedLabel === normalizedQuery;
		} else {
			return normalizedLabel.indexOf(normalizedQuery) >= 0;
		}
	};

	// --

	return (
		<LanguageaMultiSelect
			itemRenderer={itemRenderer}
			tagRenderer={tagRenderer}
			items={languages.current}
			selectedItems={selected}
			itemPredicate={itemPredicate}
			onRemove={props.onRemove}
			tagInputProps={{
				fill: true,
				rightElement: selected.length > 0 && props.onClear && <Button icon='cross' title='Clear' minimal={true} onClick={props.onClear} />,
			}}
			popoverProps={{
				minimal: true,
			}}
			placeholder='Search language'
			onItemSelect={null}
			noResults={<MenuItem disabled={true} text='No results' />}
		/>
	);
});
